import { useState } from "react";
import toast from "react-hot-toast";
import {
  useQuery,
  getAuthDomainConfig,
  updateAuthDomainConfig,
  getAuthDomainRules,
  addAuthDomainRule,
  removeAuthDomainRule,
} from "wasp/client/operations";
import { type AuthUser } from "wasp/auth";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

import DefaultLayout from "../../layout/DefaultLayout";

export default function DomainsPage({ user }: { user: AuthUser }) {
  const {
    data: config,
    isLoading: isConfigLoading,
    refetch: refetchConfig,
  } = useQuery(getAuthDomainConfig);
  const {
    data: rules,
    isLoading: isRulesLoading,
    refetch: refetchRules,
  } = useQuery(getAuthDomainRules);
  const [newDomain, setNewDomain] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);

  const defaultRule = config?.defaultRule || "ALLOW_ALL";
  const displayRuleType = defaultRule === "ALLOW_ALL" ? "BLOCK" : "ALLOW";

  const handleUpdateConfig = async (newDefaultRule: string) => {
    try {
      setIsUpdatingConfig(true);
      await updateAuthDomainConfig({ defaultRule: newDefaultRule });
      await refetchConfig();
      toast.success("Configuration updated successfully");
    } catch (err: any) {
      toast.error("Failed to update configuration");
    } finally {
      setIsUpdatingConfig(false);
    }
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain.trim()) return;

    try {
      setIsAdding(true);
      await addAuthDomainRule({ domain: newDomain, ruleType: displayRuleType });
      setNewDomain("");
      refetchRules();
      toast.success("Domain added successfully");
    } catch (err: any) {
      toast.error(err.message || "Failed to add domain");
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!window.confirm("Are you sure you want to remove this domain rule?")) {
      return;
    }

    try {
      await removeAuthDomainRule({ id });
      refetchRules();
      toast.success("Domain removed successfully");
    } catch (err: any) {
      toast.error("Failed to remove domain");
    }
  };

  const filteredRules = ((rules as any[]) || []).filter(
    (r: any) => r.ruleType === displayRuleType,
  );

  return (
    <DefaultLayout user={user}>
      <div className="w-full">
        {/* Fixed Header */}
        <div className="hidden md:flex fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-border min-h-[6rem] flex-col justify-center">
          <div className="px-6 pt-6 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Allowed Registration Domains
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Configure which email domains are permitted to register for
                  the platform.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Header (Fallback) */}
        <div className="md:hidden px-6 py-4 bg-white dark:bg-sidebar-active border-b border-border">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            Allowed Registration Domains
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Configure which email domains are permitted.
          </p>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto md:pt-[calc(var(--header-height)+2rem)]">
          <div className="w-full">
            {/* Default Policy Section */}
            <div className="px-6 py-8 border-b border-border">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Default Policy
              </h2>
              <div className="flex flex-col md:flex-row md:items-center gap-4 max-w-4xl">
                <div className="flex-1 lg:max-w-md">
                  <Select
                    value={defaultRule}
                    disabled={isConfigLoading || isUpdatingConfig}
                    onValueChange={handleUpdateConfig}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALLOW_ALL">
                        Allow All (Block List Mode)
                      </SelectItem>
                      <SelectItem value="BLOCK_ALL">
                        Block All (Allow List Mode)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground flex-1">
                  {defaultRule === "ALLOW_ALL"
                    ? "Everyone can register except domains in the block list below."
                    : "Nobody can register except domains in the allow list below."}
                </div>
              </div>
            </div>

            {/* Domains List Section */}
            <div className="py-8">
              <div className="px-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  {defaultRule === "ALLOW_ALL"
                    ? "Blocked Domains List"
                    : "Allowed Domains List"}
                </h2>

                <form
                  onSubmit={handleAdd}
                  className="flex gap-4 mb-8 max-w-2xl"
                >
                  <Input
                    placeholder="e.g., uottawa.ca"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="submit"
                    disabled={isAdding || !newDomain.trim()}
                  >
                    {isAdding ? "Adding..." : "Add Domain"}
                  </Button>
                </form>
              </div>

              <div className="w-full">
                {isRulesLoading ? (
                  <div className="text-center py-8 text-gray-500">
                    Loading...
                  </div>
                ) : filteredRules.length > 0 ? (
                  <div className="divide-y divide-border border-y border-border">
                    {filteredRules.map((d: any) => (
                      <div
                        key={d.id}
                        className="flex items-center justify-between py-4 hover:bg-muted/30 transition-colors px-6"
                      >
                        <span className="font-medium bg-muted px-2 py-1 rounded text-sm flex items-center gap-2">
                          @{d.domain}
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                              d.ruleType === "ALLOW"
                                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                            }`}
                          >
                            {d.ruleType}
                          </span>
                        </span>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleRemove(d.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground bg-muted/20 border border-dashed border-border rounded-lg mt-4">
                    No domains configured for this list.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
}
